import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { channelWa } from '@repletoryreach/sdk-api';
import { Stack, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Checkbox, Select, Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { formatMinor } from '../../lib/format';
import { MetaRefusalNotice, refusalFrom, type Refusal } from './MetaRefusalNotice';

const MUTED = 'var(--rr-color-text-muted)';
const DAYS: channelWa.CallingDay[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const ZONES = ['Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Accra', 'Africa/Cairo', 'Europe/London', 'UTC'];

type Period = { open: string; close: string };
type Week = Record<channelWa.CallingDay, { on: boolean; periods: Period[] }>;

/** The console's week from the stored hours: a day with no period is closed; a new week opens Monday to Friday, 08:00 to 17:00. */
function weekFrom(hours?: channelWa.CallingHours): Week {
  const week = {} as Week;
  const fresh = !hours || hours.slots.length === 0;
  for (const d of DAYS) {
    const periods = (hours?.slots ?? []).filter((s) => s.day === d).map((s) => ({ open: s.open, close: s.close }));
    week[d] = fresh
      ? { on: !['SATURDAY', 'SUNDAY'].includes(d), periods: [{ open: '08:00', close: '17:00' }] }
      : { on: periods.length > 0, periods: periods.length > 0 ? periods : [{ open: '08:00', close: '17:00' }] };
  }
  return week;
}

function hoursFrom(limited: boolean, timezone: string, week: Week): channelWa.CallingHours {
  return {
    enabled: limited,
    timezone,
    slots: limited ? DAYS.flatMap((d) => (week[d].on ? week[d].periods.map((p) => ({ day: d, open: p.open, close: p.close })) : [])) : []
  };
}

/**
 * WhatsApp calls for one number (owner's decision of 3 October 2026): a
 * customer presses the call button in the business's chat, and the call is
 * answered the way a phone call to the business is. The drawer says what
 * stands in the way (Meta's 2 000-a-day limit, voice not switched on, no
 * rate) before anybody presses the switch, sets the hours, and sends the
 * reader to the Voice page for who answers.
 */
export function CallingDrawer({ number, onClose }: { number: channelWa.PhoneNumber | null; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const id = number?.id ?? '';
  const calling = useQuery({ queryKey: ['number-calling', id], queryFn: () => channelWa.getCalling(id), enabled: !!number });
  const c = calling.data?.data;
  const [limited, setLimited] = useState(false);
  const [timezone, setTimezone] = useState('Africa/Johannesburg');
  const [week, setWeek] = useState<Week>(() => weekFrom());
  const [reasons, setReasons] = useState<string[] | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  useEffect(() => {
    if (!c) return;
    setLimited(c.hours.enabled);
    setTimezone(c.hours.timezone || 'Africa/Johannesburg');
    setWeek(weekFrom(c.hours));
    setReasons(null);
    setRefusal(null);
  }, [c]);

  const save = useMutation({
    mutationFn: (enabled: boolean) => channelWa.setCalling(id, { enabled, hours: hoursFrom(limited, timezone, week) }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['number-calling', id] });
      void qc.invalidateQueries({ queryKey: ['phone-numbers'] });
      setReasons(null);
      setRefusal(null);
      notifySuccess(t(res.data.status === 'on' ? 'numbers.calling.toast.on' : 'numbers.calling.toast.off'), t('nav.numbers'));
    },
    onError: (e) => {
      if (channelWa.isCallingNotReady(e)) { setReasons(e.body.error.reasons); return; }
      const r = refusalFrom(e);
      if (r) { setRefusal(r); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('toast.saveFailed'));
    }
  });

  const on = c?.status === 'on';
  const shown = reasons ?? (on ? [] : c?.reasons ?? []);
  const setDay = (d: channelWa.CallingDay, next: Partial<Week[channelWa.CallingDay]>) => setWeek((w) => ({ ...w, [d]: { ...w[d], ...next } }));
  const setPeriod = (d: channelWa.CallingDay, i: number, next: Partial<Period>) =>
    setDay(d, { periods: week[d].periods.map((p, j) => (j === i ? { ...p, ...next } : p)) });

  return (
    <Drawer opened={!!number} onClose={onClose} title={t('numbers.calling.title')} position="right" size="lg">
      <Stack gap="md" style={{ padding: 4 }} data-testid="calling-drawer">
        <Text role="bodySmall" c={MUTED}>{t('numbers.calling.intro')}</Text>
        {calling.error && <Alert color="error" role="alert">{t('numbers.calling.loadError')}</Alert>}
        {c && (
          <Group gap="xs" wrap="wrap">
            <Text role="bodySmall" fw={650}>{number?.msisdn ?? number?.display_name}</Text>
            <StatusBadge status={on ? 'healthy' : c.status === 'failed' ? 'down' : 'inactive'}>{t(`numbers.calling.status.${c.status}`)}</StatusBadge>
          </Group>
        )}
        {c?.status === 'failed' && c.error && (
          <Alert color="error" role="alert">{t(`numbers.calling.reason.${c.error}`, t('numbers.calling.reason.other'))}</Alert>
        )}
        {refusal && <MetaRefusalNotice refusal={refusal} />}
        {shown.length > 0 && (
          <Alert color="warning" title={t('numbers.calling.notReady')} data-testid="calling-reasons">
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {shown.map((r) => <li key={r}><Text role="bodySmall">{t(`numbers.calling.reason.${r}`, t('numbers.calling.reason.other'))}</Text></li>)}
            </ul>
          </Alert>
        )}
        {c?.per_minute_minor != null && (
          <Text role="bodySmall">{t('numbers.calling.price', { price: formatMinor(c.per_minute_minor) })}</Text>
        )}

        <Switch
          label={t('numbers.calling.limited')}
          description={t('numbers.calling.limitedHint')}
          checked={limited}
          onChange={setLimited}
        />
        {limited && (
          <Stack gap="sm" data-testid="calling-hours">
            <Select
              label={t('numbers.calling.timezone')}
              value={timezone}
              onChange={(e) => setTimezone(e.currentTarget.value)}
              data={(ZONES.includes(timezone) ? ZONES : [timezone, ...ZONES]).map((z) => ({ value: z, label: z }))}
            />
            {DAYS.map((d) => (
              <Stack key={d} gap={4}>
                <Checkbox label={t(`numbers.calling.day.${d}`)} checked={week[d].on} onChange={(v) => setDay(d, { on: v })} />
                {week[d].on && week[d].periods.map((p, i) => (
                  <Group key={i} gap="xs" wrap="wrap" align="flex-end">
                    <TextInput type="time" label={t('numbers.calling.opens')} value={p.open} onChange={(e) => setPeriod(d, i, { open: e.currentTarget.value })} />
                    <TextInput type="time" label={t('numbers.calling.closes')} value={p.close} onChange={(e) => setPeriod(d, i, { close: e.currentTarget.value })} />
                    {i === 1 && (
                      <Button size="xs" variant="subtle" onClick={() => setDay(d, { periods: week[d].periods.slice(0, 1) })}>{t('numbers.calling.removePeriod')}</Button>
                    )}
                  </Group>
                ))}
                {week[d].on && week[d].periods.length === 1 && (
                  <div>
                    <Button size="xs" variant="subtle" onClick={() => setDay(d, { periods: [...week[d].periods, { open: '13:00', close: '17:00' }] })}>
                      {t('numbers.calling.addPeriod')}
                    </Button>
                  </div>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {on && c?.voice_number_id && (
          <Text role="bodySmall">
            {t('numbers.calling.whoAnswers')}{' '}
            <Link to="/voice">{t('numbers.calling.whoAnswersLink')}</Link>
          </Text>
        )}

        <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Button variant="default" onClick={onClose}>{t('action.close')}</Button>
          {on && <Button variant="default" color="red" loading={save.isPending && save.variables === false} onClick={() => save.mutate(false)}>{t('numbers.calling.switchOff')}</Button>}
          <Button loading={save.isPending && save.variables === true} disabled={!c} onClick={() => save.mutate(true)}>
            {t(on ? 'numbers.calling.saveHours' : 'numbers.calling.switchOn')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
